/**
 * Conditional Logic Builder
 *
 * Visual builder for creating conditional show/hide rules
 */

import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useBuilderStore } from '../../stores/builderStore';
import { generateUUID } from '../../utils/uuid';
import type {
  ConditionalRule,
  Condition,
  ConditionOperator,
  ConditionalAction,
} from '../../types/form';

interface ConditionalLogicBuilderProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function ConditionalLogicBuilder({ isOpen, onClose }: ConditionalLogicBuilderProps) {
  const { t } = useTranslation('builder');
  const { formSchema, addCondition, updateCondition, removeCondition } = useBuilderStore();
  const [editingRule, setEditingRule] = useState<ConditionalRule | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  const OPERATORS: { value: ConditionOperator; label: string }[] = [
    { value: 'equals', label: t('conditional.operators.equals') },
    { value: 'not_equals', label: t('conditional.operators.not_equals') },
    { value: 'contains', label: t('conditional.operators.contains') },
    { value: 'not_contains', label: t('conditional.operators.not_contains') },
    { value: 'greater_than', label: t('conditional.operators.greater_than') },
    { value: 'less_than', label: t('conditional.operators.less_than') },
    { value: 'is_empty', label: t('conditional.operators.is_empty') },
    { value: 'is_not_empty', label: t('conditional.operators.is_not_empty') },
  ];

  const ACTIONS: { value: ConditionalAction; label: string; description: string }[] = [
    { value: 'show', label: t('conditional.actions.show.label'), description: t('conditional.actions.show.description') },
    { value: 'hide', label: t('conditional.actions.hide.label'), description: t('conditional.actions.hide.description') },
    { value: 'require', label: t('conditional.actions.require.label'), description: t('conditional.actions.require.description') },
    { value: 'enable', label: t('conditional.actions.enable.label'), description: t('conditional.actions.enable.description') },
    { value: 'disable', label: t('conditional.actions.disable.label'), description: t('conditional.actions.disable.description') },
  ];

  // Get fields that can be conditional targets
  const targetFields = useMemo(() => {
    return formSchema.fields.filter((f) =>
      !['hidden'].includes(f.type)
    );
  }, [formSchema.fields]);

  // Get fields that can be condition sources (have values)
  const sourceFields = useMemo(() => {
    return formSchema.fields.filter((f) =>
      !['section', 'divider', 'page_break', 'hidden', 'signature'].includes(f.type)
    );
  }, [formSchema.fields]);

  // Group rules by target
  const rulesByTarget = useMemo(() => {
    const grouped: Record<string, ConditionalRule[]> = {};
    (formSchema.conditionalLogic || []).forEach((rule) => {
      if (!grouped[rule.targetFieldId]) {
        grouped[rule.targetFieldId] = [];
      }
      grouped[rule.targetFieldId].push(rule);
    });
    return grouped;
  }, [formSchema.conditionalLogic]);

  const createNewRule = () => {
    const newRule: ConditionalRule = {
      id: generateUUID(),
      targetFieldId: targetFields[0]?.id || '',
      action: 'show',
      conditions: [
        {
          fieldId: sourceFields[0]?.id || '',
          operator: 'equals',
          value: '',
        },
      ],
      operator: 'and',
    };
    setEditingRule(newRule);
    setIsCreating(true);
  };

  const handleSaveRule = () => {
    if (!editingRule) return;

    // Validate
    if (!editingRule.targetFieldId) {
      alert(t('conditional.selectTargetField'));
      return;
    }
    if (editingRule.conditions.length === 0) {
      alert(t('conditional.addOneCondition'));
      return;
    }

    if (isCreating) {
      addCondition(editingRule);
    } else {
      updateCondition(editingRule.id, editingRule);
    }

    setEditingRule(null);
    setIsCreating(false);
  };

  const handleDeleteRule = (ruleId: string) => {
    if (confirm(t('conditional.confirmDeleteRule'))) {
      removeCondition(ruleId);
    }
  };

  const addConditionToRule = () => {
    if (!editingRule) return;
    setEditingRule({
      ...editingRule,
      conditions: [
        ...editingRule.conditions,
        {
          fieldId: sourceFields[0]?.id || '',
          operator: 'equals',
          value: '',
        },
      ],
    });
  };

  const removeConditionFromRule = (index: number) => {
    if (!editingRule) return;
    setEditingRule({
      ...editingRule,
      conditions: editingRule.conditions.filter((_, i) => i !== index),
    });
  };

  const updateConditionInRule = (index: number, updates: Partial<Condition>) => {
    if (!editingRule) return;
    setEditingRule({
      ...editingRule,
      conditions: editingRule.conditions.map((c, i) =>
        i === index ? { ...c, ...updates } : c
      ),
    });
  };

  const getFieldLabel = (fieldId: string) => {
    const field = formSchema.fields.find((f) => f.id === fieldId);
    return field?.label || t('conditional.unknownField');
  };

  const getFieldType = (fieldId: string) => {
    return formSchema.fields.find((f) => f.id === fieldId)?.type;
  };

  const renderConditionValue = (condition: Condition, index: number) => {
    const field = formSchema.fields.find((f) => f.id === condition.fieldId);
    const needsValue = !['is_empty', 'is_not_empty'].includes(condition.operator);

    if (!needsValue) return null;

    // Field has options (select, radio, checkbox)
    if (field?.options && field.options.length > 0) {
      return (
        <select
          value={String(condition.value || '')}
          onChange={(e) => updateConditionInRule(index, { value: e.target.value })}
          className="input text-sm"
        >
          <option value="">{t('conditional.selectValue')}</option>
          {field.options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      );
    }

    // Checkbox (boolean)
    if (field?.type === 'checkbox' && !field.options) {
      return (
        <select
          value={String(condition.value || '')}
          onChange={(e) => updateConditionInRule(index, { value: e.target.value === 'true' })}
          className="input text-sm"
        >
          <option value="true">{t('conditional.checked')}</option>
          <option value="false">{t('conditional.notChecked')}</option>
        </select>
      );
    }

    // Number fields
    if (['number', 'rating', 'slider', 'nps', 'opinion_scale'].includes(field?.type || '')) {
      return (
        <input
          type="number"
          value={String(condition.value || '')}
          onChange={(e) => updateConditionInRule(index, { value: parseFloat(e.target.value) || 0 })}
          className="input text-sm w-24"
          placeholder={t('conditional.value')}
        />
      );
    }

    // Default text input
    return (
      <input
        type="text"
        value={String(condition.value || '')}
        onChange={(e) => updateConditionInRule(index, { value: e.target.value })}
        className="input text-sm"
        placeholder={t('conditional.value')}
      />
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div>
            <h2 className="text-xl font-bold text-gray-900">{t('conditional.title')}</h2>
            <p className="text-sm text-gray-500">
              {t('conditional.modalDescription')}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {editingRule ? (
            // Rule Editor
            <div className="space-y-6">
              <div className="flex items-center gap-2 mb-4">
                <button
                  onClick={() => {
                    setEditingRule(null);
                    setIsCreating(false);
                  }}
                  className="p-1 hover:bg-gray-100 rounded"
                >
                  <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <h3 className="font-semibold text-lg">
                  {isCreating ? t('conditional.createNewRule') : t('conditional.editRule')}
                </h3>
              </div>

              {/* Action selector */}
              <div className="bg-gray-50 rounded-lg p-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('conditional.action')}</label>
                <div className="grid grid-cols-3 gap-2">
                  {ACTIONS.map((action) => (
                    <button
                      key={action.value}
                      onClick={() => setEditingRule({ ...editingRule, action: action.value })}
                      className={`p-3 rounded-lg border-2 text-left transition-all ${
                        editingRule.action === action.value
                          ? 'border-primary-500 bg-primary-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="font-medium text-sm">{action.label}</div>
                      <div className="text-xs text-gray-500 mt-1">{action.description}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Target field selector */}
              <div className="bg-gray-50 rounded-lg p-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t(`conditional.actions.${editingRule.action}.target`)}
                </label>
                <select
                  value={editingRule.targetFieldId}
                  onChange={(e) => setEditingRule({ ...editingRule, targetFieldId: e.target.value })}
                  className="input"
                >
                  <option value="">{t('conditional.selectField')}</option>
                  {targetFields.map((field) => (
                    <option key={field.id} value={field.id}>
                      {field.label} ({field.type})
                    </option>
                  ))}
                </select>
              </div>

              {/* Conditions */}
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <label className="block text-sm font-medium text-gray-700">
                    {t('conditional.whenConditionsMet')}
                  </label>
                  {editingRule.conditions.length > 1 && (
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-500">{t('conditional.match')}</span>
                      <select
                        value={editingRule.operator}
                        onChange={(e) => setEditingRule({ ...editingRule, operator: e.target.value as 'and' | 'or' })}
                        className="input text-sm py-1 w-24"
                      >
                        <option value="and">{t('conditional.matchAll')}</option>
                        <option value="or">{t('conditional.matchAny')}</option>
                      </select>
                    </div>
                  )}
                </div>

                <div className="space-y-3">
                  {editingRule.conditions.map((condition, index) => (
                    <div key={index} className="flex items-center gap-2 bg-white p-3 rounded-lg border">
                      {index > 0 && (
                        <span className="text-xs font-medium text-gray-400 uppercase mr-2">
                          {editingRule.operator}
                        </span>
                      )}

                      {/* Source field */}
                      <select
                        value={condition.fieldId}
                        onChange={(e) => updateConditionInRule(index, { fieldId: e.target.value, value: '' })}
                        className="input text-sm flex-1"
                      >
                        <option value="">{t('conditional.selectField')}</option>
                        {sourceFields.map((field) => (
                          <option key={field.id} value={field.id}>
                            {field.label}
                          </option>
                        ))}
                      </select>

                      {/* Operator */}
                      <select
                        value={condition.operator}
                        onChange={(e) => updateConditionInRule(index, { operator: e.target.value as ConditionOperator })}
                        className="input text-sm w-40"
                      >
                        {OPERATORS.filter((op) => {
                          // Filter operators based on field type
                          const fieldType = getFieldType(condition.fieldId);
                          if (['number', 'rating', 'slider', 'nps', 'opinion_scale'].includes(fieldType || '')) {
                            return true; // All operators work
                          }
                          if (['select', 'radio', 'checkbox'].includes(fieldType || '')) {
                            return ['equals', 'not_equals', 'is_empty', 'is_not_empty'].includes(op.value);
                          }
                          return !['greater_than', 'less_than'].includes(op.value);
                        }).map((op) => (
                          <option key={op.value} value={op.value}>
                            {op.label}
                          </option>
                        ))}
                      </select>

                      {/* Value input */}
                      <div className="flex-1">
                        {renderConditionValue(condition, index)}
                      </div>

                      {/* Remove condition */}
                      {editingRule.conditions.length > 1 && (
                        <button
                          onClick={() => removeConditionFromRule(index)}
                          className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      )}
                    </div>
                  ))}
                </div>

                <button
                  onClick={addConditionToRule}
                  className="mt-3 flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  {t('conditional.addAnotherCondition')}
                </button>
              </div>

              {/* Preview */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-medium text-blue-900 mb-2">{t('conditional.rulePreview')}</h4>
                <p className="text-sm text-blue-800">
                  <strong>{ACTIONS.find((a) => a.value === editingRule.action)?.label}</strong>
                  {' "'}
                  <strong>{getFieldLabel(editingRule.targetFieldId)}</strong>
                  {'" '}{t('conditional.when')}{' '}
                  {editingRule.conditions.map((c, i) => (
                    <span key={i}>
                      {i > 0 && <strong> {editingRule.operator.toUpperCase()} </strong>}
                      "{getFieldLabel(c.fieldId)}" {OPERATORS.find((o) => o.value === c.operator)?.label}
                      {!['is_empty', 'is_not_empty'].includes(c.operator) && ` "${c.value}"`}
                    </span>
                  ))}
                </p>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => {
                    setEditingRule(null);
                    setIsCreating(false);
                  }}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  {t('conditional.cancel')}
                </button>
                <button
                  onClick={handleSaveRule}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                >
                  {isCreating ? t('conditional.createRule') : t('conditional.saveChanges')}
                </button>
              </div>
            </div>
          ) : (
            // Rules List
            <div>
              {(formSchema.conditionalLogic || []).length === 0 ? (
                <div className="text-center py-12">
                  <svg className="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">{t('conditional.noRulesYet')}</h3>
                  <p className="text-gray-500 mb-6">
                    {t('conditional.noRulesDescription')}
                  </p>
                  <button
                    onClick={createNewRule}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    {t('conditional.createFirstRule')}
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="font-semibold text-lg">
                      {t('conditional.ruleCount', { count: (formSchema.conditionalLogic || []).length })}
                    </h3>
                    <button
                      onClick={createNewRule}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                      {t('conditional.addRule')}
                    </button>
                  </div>

                  {Object.entries(rulesByTarget).map(([targetId, rules]) => (
                    <div key={targetId} className="bg-gray-50 rounded-lg p-4">
                      <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                        <span className="w-2 h-2 bg-primary-500 rounded-full"></span>
                        {getFieldLabel(targetId)}
                      </h4>
                      <div className="space-y-2">
                        {rules.map((rule) => (
                          <div
                            key={rule.id}
                            className="bg-white rounded-lg border p-3 flex items-center justify-between group"
                          >
                            <div className="flex-1">
                              <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium mr-2 ${
                                rule.action === 'show' ? 'bg-green-100 text-green-700' :
                                rule.action === 'hide' ? 'bg-red-100 text-red-700' :
                                rule.action === 'require' ? 'bg-purple-100 text-purple-700' :
                                rule.action === 'enable' ? 'bg-blue-100 text-blue-700' :
                                'bg-gray-100 text-gray-700'
                              }`}>
                                {rule.action.toUpperCase()}
                              </span>
                              <span className="text-sm text-gray-600">
                                {t('conditional.when')}{' '}
                                {rule.conditions.map((c, i) => (
                                  <span key={i}>
                                    {i > 0 && <span className="text-gray-400"> {rule.operator} </span>}
                                    <span className="font-medium">{getFieldLabel(c.fieldId)}</span>
                                    {' '}
                                    {OPERATORS.find((o) => o.value === c.operator)?.label}
                                    {!['is_empty', 'is_not_empty'].includes(c.operator) && (
                                      <span> "{String(c.value)}"</span>
                                    )}
                                  </span>
                                ))}
                              </span>
                            </div>
                            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <button
                                onClick={() => {
                                  setEditingRule(rule);
                                  setIsCreating(false);
                                }}
                                className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                                title={t('conditional.edit')}
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                              </button>
                              <button
                                onClick={() => handleDeleteRule(rule.id)}
                                className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                                title={t('conditional.delete')}
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
